/**
 * Service for parsing YAML and calculating differences between configurations
 * Used for creating custom inputspecs with overrides and deletions
 */

// Simple YAML parser for the specific format we use
const parseYAML = (yamlString) => {
  const result = {};
  const lines = yamlString.split('\n');
  const stack = [{ obj: result, indent: -1 }];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Skip empty lines and comments
    if (!trimmed || trimmed.startsWith('#')) continue;
    
    // Calculate indentation
    const indent = line.length - line.trimStart().length;
    
    // Pop stack to find parent at correct level
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }
    
    const current = stack[stack.length - 1].obj;
    
    // Handle key-value pairs
    if (trimmed.includes(':')) {
      const colonIndex = trimmed.indexOf(':');
      const key = trimmed.substring(0, colonIndex).trim();
      const value = trimmed.substring(colonIndex + 1).trim();
      
      if (!value || value === '{}') {
        // Empty object
        current[key] = {};
        stack.push({ obj: current[key], indent });
      } else if (value) {
        // Direct value
        // Handle different value types
        if (value === '.T.' || value === 'true') {
          current[key] = true;
        } else if (value === '.F.' || value === 'false') {
          current[key] = false;
        } else if (value.includes(',')) {
          // Array-like value (e.g., "1, 1, 0, 0, 0")
          current[key] = value;
        } else if (!isNaN(value)) {
          current[key] = parseFloat(value);
        } else {
          current[key] = value;
        }
      } else {
        // Start of nested object
        current[key] = {};
        stack.push({ obj: current[key], indent });
      }
    }
  }
  
  return result;
};

// Format object back to YAML
const formatYAML = (obj, indent = 0) => {
  let yaml = '';
  const spaces = '  '.repeat(indent);
  
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      yaml += `${spaces}${key}:\n`;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      if (Object.keys(value).length === 0) {
        yaml += `${spaces}${key}: {}\n`;
      } else {
        yaml += `${spaces}${key}:\n`;
        yaml += formatYAML(value, indent + 1);
      }
    } else if (typeof value === 'boolean') {
      yaml += `${spaces}${key}: ${value ? '.T.' : '.F.'}\n`;
    } else {
      yaml += `${spaces}${key}: ${value}\n`;
    }
  }
  
  return yaml;
};

// Deep compare objects and extract differences
const calculateDiff = (originalYaml, modifiedYaml) => {
  const original = typeof originalYaml === 'string' ? parseYAML(originalYaml) : originalYaml;
  const modified = typeof modifiedYaml === 'string' ? parseYAML(modifiedYaml) : modifiedYaml;
  
  const overrides = {};
  const deletions = {};
  
  // Helper function to deep compare and find differences
  const findDifferences = (origObj, modObj, path = []) => {
    // Check for deletions (keys in original but not in modified)
    for (const key in origObj) {
      const currentPath = [...path, key];
      const pathKey = path.length > 0 ? path[0] : key;
      
      if (!(key in modObj)) {
        // This key was deleted
        if (!deletions[pathKey]) {
          if (path.length === 0) {
            // Top-level section deleted entirely
            deletions[key] = null;
          } else {
            // Nested key deleted
            deletions[pathKey] = deletions[pathKey] || [];
            if (path.length === 1) {
              deletions[pathKey].push(key);
            } else {
              // For deeply nested, we need to track the full path
              const subPath = currentPath.slice(1).join('.');
              deletions[pathKey].push(subPath);
            }
          }
        }
      } else if (typeof origObj[key] === 'object' && origObj[key] !== null && 
                 typeof modObj[key] === 'object' && modObj[key] !== null) {
        // Recursively check nested objects
        findDifferences(origObj[key], modObj[key], currentPath);
      } else if (origObj[key] !== modObj[key]) {
        // Value changed - this is an override
        if (!overrides[pathKey]) {
          overrides[pathKey] = {};
        }
        
        if (path.length === 0) {
          overrides[key] = modObj[key];
        } else if (path.length === 1) {
          overrides[pathKey][key] = modObj[key];
        } else {
          // For deeply nested overrides, build the structure
          let current = overrides[pathKey];
          for (let i = 1; i < path.length - 1; i++) {
            if (!current[path[i]]) {
              current[path[i]] = {};
            }
            current = current[path[i]];
          }
          current[key] = modObj[key];
        }
      }
    }
    
    // Check for additions (keys in modified but not in original)
    for (const key in modObj) {
      const currentPath = [...path, key];
      const pathKey = path.length > 0 ? path[0] : key;
      
      if (!(key in origObj)) {
        // This is a new key - treat as override
        if (!overrides[pathKey]) {
          overrides[pathKey] = {};
        }
        
        if (path.length === 0) {
          overrides[key] = modObj[key];
        } else if (path.length === 1) {
          overrides[pathKey][key] = modObj[key];
        } else {
          // For deeply nested additions
          let current = overrides[pathKey];
          for (let i = 1; i < path.length - 1; i++) {
            if (!current[path[i]]) {
              current[path[i]] = {};
            }
            current = current[path[i]];
          }
          current[key] = modObj[key];
        }
      }
    }
  };
  
  findDifferences(original, modified);
  
  // Clean up empty objects
  for (const key in overrides) {
    if (Object.keys(overrides[key]).length === 0) {
      delete overrides[key];
    }
  }
  
  for (const key in deletions) {
    if (Array.isArray(deletions[key]) && deletions[key].length === 0) {
      delete deletions[key];
    }
  }
  
  return { overrides, deletions };
};

// Validate YAML syntax
const validateYAML = (yamlString) => {
  try {
    const parsed = parseYAML(yamlString);
    // Basic validation - check if we got an object
    if (typeof parsed !== 'object' || parsed === null) {
      return { valid: false, error: 'Invalid YAML structure' };
    }
    return { valid: true, parsed };
  } catch (error) {
    return { valid: false, error: error.message };
  }
};

// Apply overrides and deletions to base YAML
const applyChanges = (baseYaml, overrides = {}, deletions = {}) => {
  const base = typeof baseYaml === 'string' ? parseYAML(baseYaml) : { ...baseYaml };
  
  // Apply deletions first
  for (const [section, keys] of Object.entries(deletions)) {
    if (keys === null) {
      // Delete entire section
      delete base[section];
    } else if (Array.isArray(keys) && base[section]) {
      // Delete specific keys from section
      for (const key of keys) {
        if (key.includes('.')) {
          // Handle nested deletions
          const parts = key.split('.');
          let current = base[section];
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) break;
            current = current[parts[i]];
          }
          if (current) {
            delete current[parts[parts.length - 1]];
          }
        } else {
          delete base[section][key];
        }
      }
    }
  }
  
  // Apply overrides
  for (const [section, values] of Object.entries(overrides)) {
    if (!base[section]) {
      base[section] = {};
    }
    
    // Deep merge overrides
    const deepMerge = (target, source) => {
      for (const [key, value] of Object.entries(source)) {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          if (!target[key]) {
            target[key] = {};
          }
          deepMerge(target[key], value);
        } else {
          target[key] = value;
        }
      }
    };
    
    if (typeof values === 'object' && values !== null) {
      deepMerge(base[section], values);
    } else {
      base[section] = values;
    }
  }
  
  return base;
};

// Calculate line-by-line differences for visual highlighting
const calculateLineDiffs = (originalYaml, modifiedYaml) => {
  const originalLines = originalYaml.split('\n');
  const modifiedLines = modifiedYaml.split('\n');
  
  // Create normalized versions for comparison (trim whitespace, handle boolean formats)
  const normalizeValue = (line) => {
    return line
      .trim()
      .replace(/:\s+/g, ': ')  // Normalize spacing after colons
      .replace(/\.T\./gi, 'true')
      .replace(/\.F\./gi, 'false')
      .replace(/true/gi, '.T.')
      .replace(/false/gi, '.F.');
  };
  
  // Build maps of content to line numbers for both versions
  const originalMap = new Map();
  const modifiedMap = new Map();
  
  originalLines.forEach((line, idx) => {
    const normalized = normalizeValue(line);
    if (normalized && !normalized.startsWith('#')) {
      if (!originalMap.has(normalized)) {
        originalMap.set(normalized, []);
      }
      originalMap.get(normalized).push(idx);
    }
  });
  
  modifiedLines.forEach((line, idx) => {
    const normalized = normalizeValue(line);
    if (normalized && !normalized.startsWith('#')) {
      if (!modifiedMap.has(normalized)) {
        modifiedMap.set(normalized, []);
      }
      modifiedMap.get(normalized).push(idx);
    }
  });
  
  // Determine status for each line in modified version
  const lineStatuses = modifiedLines.map((line, idx) => {
    const normalized = normalizeValue(line);
    
    // Empty or comment lines are unchanged
    if (!normalized || normalized.startsWith('#')) {
      return { lineNumber: idx + 1, status: 'unchanged', content: line };
    }
    
    // Check if this exact line exists in original
    if (originalMap.has(normalized)) {
      // Check if it's a section header or key
      if (normalized.includes(':')) {
        const key = normalized.split(':')[0];
        // Check if the key exists in both but values might be different
        const originalHasKey = originalLines.some(l => normalizeValue(l).startsWith(key + ':'));
        const isExactMatch = originalLines.some(l => normalizeValue(l) === normalized);
        
        if (isExactMatch) {
          return { lineNumber: idx + 1, status: 'unchanged', content: line };
        } else if (originalHasKey) {
          return { lineNumber: idx + 1, status: 'modified', content: line };
        }
      }
      return { lineNumber: idx + 1, status: 'unchanged', content: line };
    }
    
    // Line doesn't exist in original - it's added
    return { lineNumber: idx + 1, status: 'added', content: line };
  });
  
  // Find deleted sections (exist in original but not in modified)
  const deletedSections = [];
  const modifiedKeys = new Set();
  
  // Extract top-level keys from modified
  modifiedLines.forEach(line => {
    const trimmed = line.trim();
    if (trimmed && !trimmed.startsWith(' ') && trimmed.includes(':')) {
      const key = trimmed.split(':')[0];
      modifiedKeys.add(key);
    }
  });
  
  // Check for deleted top-level sections
  originalLines.forEach(line => {
    const trimmed = line.trim();
    if (trimmed && !trimmed.startsWith(' ') && trimmed.includes(':')) {
      const key = trimmed.split(':')[0];
      if (!modifiedKeys.has(key) && key !== 'DATA' && key !== 'FMOHYB' && key !== 'FMOXYZ' && key !== 'FMOBND') {
        // Skip empty sections that are structural
        deletedSections.push(key);
      }
    }
  });
  
  return {
    lineStatuses,
    deletedSections
  };
};

export const yamlDiffService = {
  parseYAML,
  formatYAML,
  calculateDiff,
  validateYAML,
  applyChanges,
  calculateLineDiffs
};